import { forwardRef, memo, type MouseEventHandler, type Component } from 'react';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import CardActions, { type CardActionsProps } from '@mui/material/CardActions';
import DialogActions, { type DialogActionsProps } from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
// SCSS imports
import { footer, builder, builderPending, quickTour, prevButton, nextButton } from './BuilderFooter.module.scss';

type BuilderFooterProps = {
  readonly tour?: boolean;
  readonly onPrev: MouseEventHandler<HTMLButtonElement>;
  readonly onNext: MouseEventHandler<HTMLButtonElement>;
  readonly isFirst?: boolean;
  readonly isLast?: boolean;
  readonly disabledPrev?: boolean;
  readonly disabledNext?: boolean;
  readonly topPadding?: boolean;
  readonly pendingPrev?: boolean;
  readonly pendingNext?: boolean;
  readonly prevCaption?: string;
  readonly nextCaption?: string;
};

const BuilderFooter = forwardRef<Component<DialogActionsProps | CardActionsProps>, BuilderFooterProps>(({
  tour = false,
  onPrev,
  onNext,
  isFirst = false,
  isLast = false,
  disabledPrev = false,
  disabledNext = false,
  topPadding = false,
  pendingPrev = false,
  pendingNext = false,
  prevCaption,
  nextCaption
  // eslint-disable-next-line complexity
}, ref) => {
  const Actions = tour ? DialogActions : CardActions;
  return (
    <Actions
        ref={ref}
        disableSpacing={tour ? true : undefined}
        className={clsx(footer, {
          [builder]: !tour && !topPadding,
          [builderPending]: !tour && topPadding,
          [quickTour]: tour
        })}
    >
      <Button
          onClick={onPrev}
          color="primary"
          variant="outlined"
          size="large"
          disabled={disabledPrev || pendingPrev}
          startIcon={pendingPrev ? <CircularProgress size={18} color="inherit"/> : undefined}
          className={prevButton}
      >
        <FormattedMessage
            id={prevCaption || (tour
              ? (isFirst && 'common.button.close') || 'common.button.back'
              : 'common.button.skip'
            )}
            defaultMessage={prevCaption}
        />
      </Button>
      <Button
          onClick={onNext}
          color="primary"
          variant="contained"
          disableElevation
          size="large"
          disabled={disabledNext || pendingNext}
          startIcon={pendingNext ? <CircularProgress size={18} color="inherit"/> : undefined}
          className={nextButton}
      >
        <FormattedMessage
            id={nextCaption || (tour
              ? (isLast && 'common.button.finish') || 'common.button.next'
              : 'common.button.confirm'
            )}
            defaultMessage={nextCaption}
        />
      </Button>
    </Actions>
  );
});

BuilderFooter.displayName = 'BuilderFooter';

export default memo(BuilderFooter);
